import { useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { COINS_COLLECTION, COUPONS_COLLECTION, BUSINESSES_COLLECTION, builtInCoupons, normalizeCoupon } from "../Data/offers";

// The signed-in customer's wallet, live from Coins/{email or phone}: coins and
// how often each code was used. Only the server writes it
// (api/place-order.js); a guest has an empty one. priorOrders says whether
// the account has ordered before, for first-order codes.
export function useWallet() {
    const [wallet, setWallet] = useState({ key: "", balance: 0, couponUses: {}, priorOrders: 0 });
    useEffect(() => {
        let stop = () => {};
        const off = auth.onAuthStateChanged((u) => {
            stop();
            const key = u && !u.isAnonymous ? (u.email || u.phoneNumber) : "";
            if (!key) { setWallet({ key: "", balance: 0, couponUses: {}, priorOrders: 0 }); stop = () => {}; return; }
            setWallet((w) => ({ ...w, key }));
            stop = db.collection(COINS_COLLECTION).doc(key).onSnapshot(
                (snap) => {
                    const d = (snap.exists && snap.data()) || {};
                    setWallet((w) => ({ ...w, key, balance: Math.max(0, Math.floor(Number(d.balance) || 0)), couponUses: d.couponUses || {} }));
                },
                () => setWallet((w) => ({ ...w, key, balance: 0, couponUses: {} })),
            );
            // Orders are written with the account's email or phone.
            Promise.all(["email", "userEmail", "phone"].map((f) => db.collection("Orders").where(f, "==", key).limit(1).get().then((s) => s.size).catch(() => 0)))
                .then((sizes) => setWallet((w) => (w.key === key ? { ...w, priorOrders: Math.max(...sizes) } : w)));
        });
        return () => { off(); stop(); };
    }, []);
    return wallet;
}

// Just the coin balance.
export default function useCoinBalance() {
    return useWallet().balance;
}

// The codes on offer, live from Coupons (made in the admin panel). Until the
// admin panel has saved any, or if they cannot be read, the built-in offers.
export function useCoupons() {
    const [coupons, setCoupons] = useState(builtInCoupons);
    useEffect(() => db.collection(COUPONS_COLLECTION).onSnapshot(
        (snap) => {
            const list = [];
            snap.forEach((d) => list.push(normalizeCoupon(d.data(), d.id)));
            setCoupons(list.length ? list : builtInCoupons());
        },
        () => setCoupons(builtInCoupons()),
    ), []);
    return coupons;
}

// The signed-in customer's business account, live from Businesses/{email or
// phone}: null until they apply. `status` is set only by the admin panel
// (pending, verified, rejected, paused). One listener is shared by every
// component that asks (each product card asks, for showroom prices).
let bizState = { key: "", loaded: false, biz: null };
const bizSubs = new Set();
let bizStop = null;
const setBiz = (next) => { bizState = next; bizSubs.forEach((fn) => fn(next)); };
const startBiz = () => {
    let stopDoc = () => {};
    const off = auth.onAuthStateChanged((u) => {
        stopDoc();
        const key = u && !u.isAnonymous ? (u.email || u.phoneNumber) : "";
        if (!key) { setBiz({ key: "", loaded: true, biz: null }); stopDoc = () => {}; return; }
        stopDoc = db.collection(BUSINESSES_COLLECTION).doc(key).onSnapshot(
            (snap) => setBiz({ key, loaded: true, biz: snap.exists ? { id: snap.id, ...snap.data() } : null }),
            () => setBiz({ key, loaded: true, biz: null }),
        );
    });
    return () => { off(); stopDoc(); };
};

export function useBusiness() {
    const [state, setState] = useState(bizState);
    useEffect(() => {
        bizSubs.add(setState);
        if (!bizStop) bizStop = startBiz();
        setState(bizState);
        return () => {
            bizSubs.delete(setState);
            if (!bizSubs.size && bizStop) { bizStop(); bizStop = null; }
        };
    }, []);
    return state;
}
